import { ChevronDown } from 'lucide-react'
import { useId, useMemo, useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'

import { useAuthStore } from '@/stores/authStore'
import { cn } from '@/utils/cn'

import { Brand } from './Brand'
import { NAV_SECTIONS, type NavItem, type NavSection } from './navItems'
import { secaoDaRota, secoesVisiveis } from './navSections'

interface SidebarProps {
  /** Só ícones (desktop recolhido). */
  mini?: boolean
  onNavigate?: () => void
  /** Pede para expandir o painel (clique numa seção no modo mini, ex.: tablet sem hover). */
  onExpandRequest?: () => void
}

export function Sidebar({ mini = false, onNavigate, onExpandRequest }: SidebarProps) {
  const papeis = useAuthStore((state) => state.user?.papeis)
  const { pathname } = useLocation()
  const sections = useMemo(() => secoesVisiveis(NAV_SECTIONS, papeis), [papeis])
  const ativa = secaoDaRota(sections, pathname)

  // Seções começam recolhidas; só a da rota atual abre (e reabre ao navegar para outra).
  const [abertas, setAbertas] = useState<ReadonlySet<string>>(() => new Set(ativa ? [ativa] : []))
  const [ativaAnterior, setAtivaAnterior] = useState(ativa)
  if (ativa !== ativaAnterior) {
    setAtivaAnterior(ativa)
    if (ativa) setAbertas((prev) => new Set(prev).add(ativa))
  }

  const setAberta = (id: string, aberta: boolean) =>
    setAbertas((prev) => {
      const next = new Set(prev)
      if (aberta) next.add(id)
      else next.delete(id)
      return next
    })

  return (
    <div className="flex h-full flex-col bg-sidebar text-sidebar-fg">
      <div
        className={cn(
          'flex h-16 shrink-0 items-center border-b border-white/8',
          mini ? 'justify-center' : 'px-5',
        )}
      >
        <Brand surface="dark" size="sm" markOnly={mini} />
      </div>

      <nav
        aria-label="Menu principal"
        className={cn(
          'pc-no-scrollbar flex-1 overflow-y-auto overflow-x-hidden py-4',
          mini ? 'space-y-2 px-2' : 'space-y-1 px-3',
        )}
      >
        {sections.map((section) =>
          section.title ? (
            <NavSectionGroup
              key={section.id}
              section={section}
              mini={mini}
              aberta={abertas.has(section.id)}
              ativa={section.id === ativa}
              onToggle={() => setAberta(section.id, !abertas.has(section.id))}
              onMiniClick={() => {
                setAberta(section.id, true)
                onExpandRequest?.()
              }}
              onNavigate={onNavigate}
            />
          ) : (
            section.items.map((item) => (
              <NavItemLink key={item.to} item={item} mini={mini} onNavigate={onNavigate} />
            ))
          ),
        )}
      </nav>
    </div>
  )
}

interface NavItemLinkProps {
  item: NavItem
  mini: boolean
  /** Item dentro de uma seção (recuado, ao lado da linha-guia). */
  nested?: boolean
  onNavigate?: () => void
}

function NavItemLink({ item, mini, nested, onNavigate }: NavItemLinkProps) {
  return (
    <NavLink
      to={item.to}
      end={item.end}
      onClick={onNavigate}
      title={mini ? item.label : undefined}
      aria-label={mini ? item.label : undefined}
      className={({ isActive }) =>
        cn(
          'relative flex items-center gap-3 rounded-lg text-sm font-medium transition-colors',
          mini ? 'mx-auto size-11 justify-center' : nested ? 'h-9 px-3' : 'h-10 px-3',
          isActive
            ? 'bg-white/12 text-white'
            : 'text-sidebar-muted hover:bg-white/6 hover:text-white',
        )
      }
    >
      {({ isActive }) => (
        <>
          {isActive && (
            <span
              aria-hidden
              className={cn(
                'absolute top-1/2 h-5 w-1 -translate-y-1/2 rounded-full bg-brand',
                nested ? '-left-[0.8rem]' : 'left-0',
              )}
            />
          )}
          <item.icon className={cn('shrink-0', nested ? 'size-4' : 'size-[1.15rem]')} />
          {!mini && <span className="truncate whitespace-nowrap">{item.label}</span>}
        </>
      )}
    </NavLink>
  )
}

interface NavSectionGroupProps {
  section: NavSection
  mini: boolean
  aberta: boolean
  /** Contém a rota atual. */
  ativa: boolean
  onToggle: () => void
  onMiniClick: () => void
  onNavigate?: () => void
}

function NavSectionGroup({
  section,
  mini,
  aberta,
  ativa,
  onToggle,
  onMiniClick,
  onNavigate,
}: NavSectionGroupProps) {
  const listaId = useId()
  const Icon = section.icon

  if (mini) {
    return (
      <button
        type="button"
        onClick={onMiniClick}
        title={section.title}
        aria-label={section.title}
        className={cn(
          'relative mx-auto grid size-11 place-items-center rounded-lg transition-colors',
          ativa
            ? 'bg-brand/90 text-white shadow-sm'
            : 'text-sidebar-muted hover:bg-white/6 hover:text-white',
        )}
      >
        {Icon && <Icon className="size-[1.15rem]" />}
      </button>
    )
  }

  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={aberta}
        aria-controls={listaId}
        className={cn(
          'flex h-10 w-full items-center gap-3 rounded-lg px-2 text-sm font-semibold transition-colors hover:bg-white/6',
          ativa ? 'text-white' : 'text-sidebar-fg/85',
        )}
      >
        {Icon && (
          <span
            className={cn(
              'grid size-7 shrink-0 place-items-center rounded-md transition-colors',
              ativa ? 'bg-brand text-white' : 'bg-white/8 text-sidebar-muted',
            )}
          >
            <Icon className="size-4" />
          </span>
        )}
        <span className="flex-1 truncate whitespace-nowrap text-left">{section.title}</span>
        <ChevronDown
          className={cn(
            'size-4 shrink-0 text-sidebar-muted transition-transform duration-200 motion-reduce:transition-none',
            aberta && 'rotate-180',
          )}
        />
      </button>

      {/* grid-rows 0fr→1fr anima a altura sem medir o conteúdo. */}
      <div
        id={listaId}
        inert={!aberta}
        className={cn(
          'grid transition-[grid-template-rows] duration-200 ease-out motion-reduce:transition-none',
          aberta ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
        )}
      >
        <div className="overflow-hidden">
          <div className="mb-2 ml-[1.35rem] mt-1 space-y-0.5 border-l border-white/10 pl-3">
            {section.items.map((item) => (
              <NavItemLink key={item.to} item={item} mini={false} nested onNavigate={onNavigate} />
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
